/**
 * Deep device/browser fingerprinting.
 * Runs once on app mount, caches in memory, enriches every track() payload.
 *
 * Captures:
 *  - Precise geolocation (lat/long) with permission
 *  - WebGL GPU vendor / renderer
 *  - Canvas & audio fingerprints (unique per device)
 *  - Battery level / charging
 *  - WebRTC local IP
 *  - OS family (android/ios/mac/linux/windows/chromeos)
 *  - Touch / pointer / hover capabilities
 *  - Installed fonts heuristic
 *  - Plugin count
 *  - Storage quota
 *  - Media devices count
 *  - Performance / memory
 */

export type DeepContext = {
  // Geolocation (if granted)
  geo_latitude?: number
  geo_longitude?: number
  geo_accuracy_m?: number
  geo_altitude?: number | null
  geo_heading?: number | null
  geo_speed?: number | null
  geo_permission?: 'granted' | 'denied' | 'prompt' | 'unknown'

  // OS classification
  os_family?: 'android' | 'ios' | 'mac' | 'windows' | 'linux' | 'chromeos' | 'other'
  os_ua_ch?: Record<string, any>   // UA Client Hints (high-entropy)

  // Hardware
  gpu_vendor?: string
  gpu_renderer?: string
  webgl_version?: string
  canvas_fp?: string
  audio_fp?: string
  battery_level?: number
  battery_charging?: boolean
  battery_charging_time?: number
  battery_discharging_time?: number

  // Network
  webrtc_local_ip?: string
  network_type?: string
  network_save_data?: boolean

  // Capabilities
  touch_support?: boolean
  pointer_coarse?: boolean
  hover_support?: boolean
  plugin_count?: number
  font_count_estimate?: number
  storage_quota_mb?: number
  storage_usage_mb?: number
  media_device_count?: number
  media_kinds?: string      // "audioinput,videoinput,audiooutput"
  webdriver?: boolean
  pdf_viewer_enabled?: boolean

  // Display
  screen_orientation?: string
  screen_avail_width?: number
  screen_avail_height?: number

  // Memory / perf
  performance_memory_used_mb?: number
  performance_memory_total_mb?: number
  performance_memory_limit_mb?: number
}

// ------------------------------------------------------------
// OS classification (prefers UA Client Hints, falls back to UA string)
// ------------------------------------------------------------
async function getOSInfo(): Promise<{ family: DeepContext['os_family']; ua_ch?: any }> {
  const ua = navigator.userAgent.toLowerCase()
  const uaData = (navigator as any).userAgentData
  let hints: any = undefined
  if (uaData && typeof uaData.getHighEntropyValues === 'function') {
    try {
      hints = await uaData.getHighEntropyValues([
        'architecture', 'bitness', 'model', 'platform', 'platformVersion',
        'uaFullVersion', 'fullVersionList', 'wow64', 'formFactor',
      ])
    } catch {}
  }

  const platform = (hints?.platform || navigator.platform || '').toLowerCase()
  let family: DeepContext['os_family'] = 'other'
  if (/android/.test(ua)) family = 'android'
  else if (/iphone|ipad|ipod|ios/.test(ua) || (/mac/.test(platform) && navigator.maxTouchPoints > 1)) family = 'ios'
  else if (/mac/.test(platform) || /mac os/.test(ua)) family = 'mac'
  else if (/win/.test(platform) || /windows/.test(ua)) family = 'windows'
  else if (/cros/.test(ua) || /chromeos/.test(platform)) family = 'chromeos'
  else if (/linux|x11/.test(ua) || /linux/.test(platform)) family = 'linux'
  return { family, ua_ch: hints }
}

// ------------------------------------------------------------
// Geolocation (user-gated; never blocks)
// ------------------------------------------------------------
async function getGeolocation(): Promise<Partial<DeepContext>> {
  if (!('geolocation' in navigator)) return { geo_permission: 'unknown' }

  // Check permission first so we don't prompt on every page
  let permState: DeepContext['geo_permission'] = 'unknown'
  if ('permissions' in navigator) {
    try {
      const p = await (navigator as any).permissions.query({ name: 'geolocation' })
      permState = p.state
    } catch {}
  }

  // If denied, don't call — just record.
  if (permState === 'denied') return { geo_permission: 'denied' }

  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve({ geo_permission: permState }), 4000)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timeout)
        resolve({
          geo_permission: 'granted',
          geo_latitude: pos.coords.latitude,
          geo_longitude: pos.coords.longitude,
          geo_accuracy_m: pos.coords.accuracy,
          geo_altitude: pos.coords.altitude,
          geo_heading: pos.coords.heading,
          geo_speed: pos.coords.speed,
        })
      },
      () => {
        clearTimeout(timeout)
        resolve({ geo_permission: permState === 'prompt' ? 'denied' : permState })
      },
      { enableHighAccuracy: true, timeout: 3500, maximumAge: 300_000 }
    )
  })
}

// ------------------------------------------------------------
// WebGL GPU
// ------------------------------------------------------------
function getWebGL(): Partial<DeepContext> {
  try {
    const canvas = document.createElement('canvas')
    const gl = (canvas.getContext('webgl2') || canvas.getContext('webgl')) as WebGLRenderingContext | null
    if (!gl) return {}
    const dbg = gl.getExtension('WEBGL_debug_renderer_info')
    return {
      gpu_vendor: dbg ? gl.getParameter((dbg as any).UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR),
      gpu_renderer: dbg ? gl.getParameter((dbg as any).UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
      webgl_version: gl.getParameter(gl.VERSION),
    }
  } catch {
    return {}
  }
}

// ------------------------------------------------------------
// Canvas fingerprint
// ------------------------------------------------------------
function getCanvasFP(): string | undefined {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 200; canvas.height = 50
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.textBaseline = 'top'
    ctx.font = "14px 'Arial'"
    ctx.fillStyle = '#f60'
    ctx.fillRect(125, 1, 62, 20)
    ctx.fillStyle = '#069'
    ctx.fillText('Cloudsize·fp·🛰', 2, 15)
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)'
    ctx.fillText('Cloudsize·fp·🛰', 4, 17)
    return hashString(canvas.toDataURL())
  } catch {
    return undefined
  }
}

// ------------------------------------------------------------
// Audio fingerprint (tiny offline context)
// ------------------------------------------------------------
function getAudioFP(): Promise<string | undefined> {
  return new Promise((resolve) => {
    try {
      const Ctx = (window.OfflineAudioContext || (window as any).webkitOfflineAudioContext)
      if (!Ctx) return resolve(undefined)
      const ctx = new Ctx(1, 44100, 44100)
      const osc = ctx.createOscillator()
      osc.type = 'triangle'
      osc.frequency.value = 10_000
      const comp = ctx.createDynamicsCompressor()
      comp.threshold.value = -50
      comp.knee.value = 40
      comp.ratio.value = 12
      comp.attack.value = 0
      comp.release.value = 0.25
      osc.connect(comp)
      comp.connect(ctx.destination)
      osc.start(0)
      ctx.startRendering().then((buf) => {
        let sum = 0
        const data = buf.getChannelData(0)
        for (let i = 4500; i < 5000; i++) sum += Math.abs(data[i])
        resolve(sum.toString())
      }).catch(() => resolve(undefined))
      setTimeout(() => resolve(undefined), 800)
    } catch { resolve(undefined) }
  })
}

// ------------------------------------------------------------
// Battery
// ------------------------------------------------------------
async function getBattery(): Promise<Partial<DeepContext>> {
  try {
    const b: any = await (navigator as any).getBattery?.()
    if (!b) return {}
    return {
      battery_level: b.level,
      battery_charging: b.charging,
      battery_charging_time: b.chargingTime === Infinity ? -1 : b.chargingTime,
      battery_discharging_time: b.dischargingTime === Infinity ? -1 : b.dischargingTime,
    }
  } catch { return {} }
}

// ------------------------------------------------------------
// WebRTC local IP
// ------------------------------------------------------------
function getWebRTCLocalIP(): Promise<string | undefined> {
  return new Promise((resolve) => {
    try {
      const RTCPC = (window as any).RTCPeerConnection || (window as any).webkitRTCPeerConnection
      if (!RTCPC) return resolve(undefined)
      const pc = new RTCPC({ iceServers: [] })
      pc.createDataChannel('')
      pc.onicecandidate = (e: any) => {
        if (!e.candidate) { pc.close(); resolve(undefined); return }
        const m = /(\d{1,3}(\.\d{1,3}){3}|[a-f0-9:]+)/.exec(e.candidate.candidate)
        if (m) { resolve(m[1]); pc.close() }
      }
      pc.createOffer().then((offer: any) => pc.setLocalDescription(offer)).catch(() => resolve(undefined))
      setTimeout(() => { try { pc.close() } catch {}; resolve(undefined) }, 1500)
    } catch { resolve(undefined) }
  })
}

// ------------------------------------------------------------
// Fonts (heuristic)
// ------------------------------------------------------------
function estimateFontCount(): number {
  const candidates = [
    'Arial', 'Arial Black', 'Courier', 'Courier New', 'Georgia', 'Helvetica',
    'Impact', 'Monaco', 'Palatino', 'Tahoma', 'Times', 'Times New Roman',
    'Trebuchet MS', 'Verdana', 'Calibri', 'Cambria', 'Consolas', 'Segoe UI',
    'Roboto', 'Noto Sans', 'Ubuntu', 'SF Pro', 'San Francisco', 'Menlo',
    'Lucida Console', 'Garamond', 'Futura', 'Gill Sans', 'Optima', 'Copperplate',
  ]
  try {
    const baseFonts = ['monospace', 'sans-serif', 'serif']
    const testString = 'mmmmmmmmmmlli'
    const body = document.body
    const span = document.createElement('span')
    span.style.cssText = 'position:absolute;left:-9999px;font-size:72px;'
    span.textContent = testString
    body.appendChild(span)
    const base: Record<string, [number, number]> = {}
    for (const f of baseFonts) {
      span.style.fontFamily = f
      base[f] = [span.offsetWidth, span.offsetHeight]
    }
    let found = 0
    for (const f of candidates) {
      for (const b of baseFonts) {
        span.style.fontFamily = `'${f}',${b}`
        if (span.offsetWidth !== base[b][0] || span.offsetHeight !== base[b][1]) {
          found++; break
        }
      }
    }
    body.removeChild(span)
    return found
  } catch { return 0 }
}

// ------------------------------------------------------------
// Storage + media + misc
// ------------------------------------------------------------
async function getStorage(): Promise<Partial<DeepContext>> {
  try {
    if ('storage' in navigator && (navigator as any).storage.estimate) {
      const est = await (navigator as any).storage.estimate()
      return {
        storage_quota_mb: est.quota ? Math.round(est.quota / 1048576) : undefined,
        storage_usage_mb: est.usage ? Math.round(est.usage / 1048576) : undefined,
      }
    }
  } catch {}
  return {}
}

async function getMedia(): Promise<Partial<DeepContext>> {
  try {
    if (navigator.mediaDevices?.enumerateDevices) {
      const devs = await navigator.mediaDevices.enumerateDevices()
      return {
        media_device_count: devs.length,
        media_kinds: Array.from(new Set(devs.map((d) => d.kind))).join(','),
      }
    }
  } catch {}
  return {}
}

function getPerfMemory(): Partial<DeepContext> {
  const m = (performance as any).memory
  if (!m) return {}
  return {
    performance_memory_used_mb: Math.round(m.usedJSHeapSize / 1048576),
    performance_memory_total_mb: Math.round(m.totalJSHeapSize / 1048576),
    performance_memory_limit_mb: Math.round(m.jsHeapSizeLimit / 1048576),
  }
}

function getCapabilities(): Partial<DeepContext> {
  return {
    touch_support: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    pointer_coarse: matchMedia('(pointer: coarse)').matches,
    hover_support: matchMedia('(hover: hover)').matches,
    plugin_count: navigator.plugins?.length || 0,
    webdriver: !!(navigator as any).webdriver,
    pdf_viewer_enabled: (navigator as any).pdfViewerEnabled ?? false,
    screen_orientation: screen.orientation?.type,
    screen_avail_width: screen.availWidth,
    screen_avail_height: screen.availHeight,
    network_save_data: (navigator as any).connection?.saveData,
    network_type: (navigator as any).connection?.type,
  }
}

function hashString(s: string): string {
  let h = 0
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h) + s.charCodeAt(i) | 0
  // include length to reduce collisions
  return `${s.length.toString(36)}_${(h >>> 0).toString(36)}`
}

// ------------------------------------------------------------
// Public: gather everything, memoize
// ------------------------------------------------------------
let cached: DeepContext | null = null
let inflight: Promise<DeepContext> | null = null

export function getCachedDeepContext(): DeepContext {
  return cached || {}
}

export async function gatherDeepContext(): Promise<DeepContext> {
  if (cached) return cached
  if (inflight) return inflight
  inflight = (async () => {
    const [os, geo, battery, audioFp, webrtcIp, storage, media] = await Promise.all([
      getOSInfo(),
      getGeolocation(),
      getBattery(),
      getAudioFP(),
      getWebRTCLocalIP(),
      getStorage(),
      getMedia(),
    ])
    const ctx: DeepContext = {
      ...getCapabilities(),
      ...getWebGL(),
      ...getPerfMemory(),
      ...geo,
      ...battery,
      ...storage,
      ...media,
      os_family: os.family,
      os_ua_ch: os.ua_ch,
      audio_fp: audioFp,
      canvas_fp: getCanvasFP(),
      webrtc_local_ip: webrtcIp,
      font_count_estimate: estimateFontCount(),
    }
    cached = ctx
    return ctx
  })()
  return inflight
}
